// frontend/src/pages/HomePage.tsx

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  SparklesIcon,
  HeartIcon,
  TruckIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ClockIcon,
  EnvelopeIcon,
  FilmIcon,
  PlayIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  XMarkIcon,
  CakeIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import api, { Product, Slide } from '../../services/api';

interface ProductWithRating extends Product {
  ratingSummary?: {
    average_rating: number;
    total_reviews: number;
  };
}

// Preview Modal Component
interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'video' | 'pdf';
  title?: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ isOpen, onClose, mediaUrl, mediaType, title }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    if (isOpen && mediaType === 'video' && videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, [isOpen, mediaType]);

  if (!isOpen) return null;

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto overflow-x-hidden">
      <div className="flex items-center justify-center min-h-screen px-4 py-4 text-center">
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        <div className="relative inline-block w-full max-w-5xl align-middle transform transition-all">
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl overflow-hidden">
            <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center px-4 py-3 bg-gradient-to-r from-black/50 to-transparent">
              {title && (
                <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                  {title}
                </h3>
              )}
              <button
                onClick={onClose}
                className="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-white" />
              </button>
            </div>
            <div className="flex items-center justify-center min-h-[50vh] max-h-[85vh] p-4">
              {mediaType === 'image' ? (
                <img
                  src={mediaUrl}
                  alt={title || 'Preview'}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                  onClick={onClose}
                />
              ) : mediaType === 'video' ? (
                <div className="relative max-w-full max-h-[80vh]">
                  <video
                    ref={videoRef}
                    src={mediaUrl}
                    className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
                    controls
                    controlsList="nodownload"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="text-center">
                  <DocumentChartBarIcon className="h-24 w-24 text-white/50 mx-auto mb-4" />
                  <p className="text-white/70 mb-4">Size Chart Document</p>
                  <a
                    href={mediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#2D6A4F] text-white rounded-lg hover:bg-[#1E4A37] transition-colors inline-flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Open PDF
                  </a>
                </div>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-gradient-to-t from-black/50 to-transparent text-center">
              <p className="text-white/70 text-xs">Click outside or press ESC to close</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Media Item Type
interface MediaItem {
  type: 'image' | 'video' | 'pdf';
  url: string;
  thumbnail?: string;
  title?: string;
}

/* ═══════════════════════════════════════
   PRODUCT CARD WITH MEDIA SLIDER
═══════════════════════════════════════ */
interface PCProps {
  product: ProductWithRating;
  idx: number;
  curMediaIdx: { [k: number]: number };
  wishlist: number[];
  getImageUrl: (u: string) => string;
  toggleWishlist: (e: React.MouseEvent, id: number) => void;
  prevMedia: (e: React.MouseEvent, id: number, total: number) => void;
  nextMedia: (e: React.MouseEvent, id: number, total: number) => void;
  gotoMedia: (e: React.MouseEvent, id: number, i: number) => void;
  productRatings?: Record<number, { average: number; count: number }>;
  onMediaClick: (url: string, type: 'image' | 'video' | 'pdf', title: string) => void;
}

const ProductCard: React.FC<PCProps> = ({
  product, idx, curMediaIdx, wishlist,
  getImageUrl, toggleWishlist, prevMedia, nextMedia, gotoMedia,
  productRatings = {}, onMediaClick
}) => {
  const mediaItems: MediaItem[] = [];
  (product.images || []).forEach(img => {
    mediaItems.push({ type: 'image', url: img.image_url, title: `${product.name} - Image` });
  });
  (product.videos || []).forEach(video => {
    mediaItems.push({ type: 'video', url: video.video_url, thumbnail: video.thumbnail_url, title: `${product.name} - Video` });
  });
  if ((product as any).size_chart) {
    mediaItems.push({ type: 'pdf', url: (product as any).size_chart, title: `${product.name} - Size Chart` });
  }

  const cur = curMediaIdx[product.id] || 0;
  const hasMulti = mediaItems.length > 1;
  const currentMedia = mediaItems[cur];
  const disc = product.mrp && product.selling_price && product.mrp > product.selling_price
    ? Math.round(((product.mrp - product.selling_price) / product.mrp) * 100) : 0;
  const inWish = wishlist.includes(product.id);
  const rating = productRatings[product.id] || { average: 0, count: 0 };

  const handleMediaClick = () => {
    if (currentMedia) {
      onMediaClick(getImageUrl(currentMedia.url), currentMedia.type, currentMedia.title || product.name);
    }
  };

  const save = disc > 0 ? Math.round(product.mrp - product.selling_price) : 0;
  const rawAge: any = (product as any).age_group;
  const ageGroupName: string = !rawAge
    ? ''
    : typeof rawAge === 'object'
      ? (rawAge.name || rawAge.range || '')
      : (/^\d+$/.test(String(rawAge)) ? '' : String(rawAge)); // hide raw numeric ids
  const lowStock = product.stock > 0 && product.stock <= (product.low_stock_alert || 5);

  return (
    <div
      className={`prod-card anim-up anim-d${Math.min(idx + 1, 6)}`}
      style={{ width: '100%', maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Link to={`/products/${product.id}`} style={{ display: 'block', textDecoration: 'none' }} onClick={() => window.scrollTo(0, 0)}>
        <div className="prod-media">
          <div
            className="prod-img-track"
            style={{ display: 'flex', width: '100%', height: '100%', transform: `translateX(-${cur * 100}%)`, transition: 'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)', cursor: 'pointer' }}
            onClick={handleMediaClick}
          >
            {mediaItems.map((media, ii) => (
              <div key={ii} style={{ width: '100%', height: '100%', flexShrink: 0, position: 'relative' }}>
                {media.type === 'image' && (
                  <img
                    src={getImageUrl(media.url)}
                    alt={`${product.name} ${ii + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                    onError={e => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=No+Image'; }}
                  />
                )}
                {media.type === 'video' && (
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <img
                      src={media.thumbnail || getImageUrl(media.url)}
                      alt={`Video ${ii + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=Video'; }}
                    />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(31,23,51,0.28)' }}>
                      <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }}>
                        <PlayIcon style={{ width: 22, height: 22, color: '#8b5cf6', marginLeft: 2 }} />
                      </div>
                    </div>
                  </div>
                )}
                {media.type === 'pdf' && (
                  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)' }}>
                    <DocumentChartBarIcon style={{ width: 44, height: 44, color: '#ea580c' }} />
                    <span style={{ fontSize: 11, color: '#9a3412', marginTop: 8, fontWeight: 700 }}>Size Chart</span>
                  </div>
                )}
                {media.type !== 'image' && (
                  <div className="prod-chip-dark" style={{ position: 'absolute', bottom: 10, left: 10 }}>
                    {media.type === 'video' ? <FilmIcon style={{ width: 10, height: 10 }} /> : <DocumentTextIcon style={{ width: 10, height: 10 }} />}
                    {media.type === 'video' ? 'Video' : 'Size Chart'}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', zIndex: 5 }}>
            {product.is_featured && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '4px 9px', background: 'linear-gradient(135deg,#ec4899,#8b5cf6)', borderRadius: 999, fontSize: 9.5, color: 'white', fontWeight: 700, boxShadow: '0 4px 12px rgba(139,92,246,0.35)' }}>
                <StarIconSolid style={{ width: 8, height: 8 }} /> Top
              </span>
            )}
            {disc >= 10 && (
              <span style={{ padding: '4px 9px', background: '#1f1733', borderRadius: 999, fontSize: 9.5, color: 'white', fontWeight: 700, letterSpacing: '0.3px' }}>{disc}% OFF</span>
            )}
          </div>

          <button
            onClick={e => toggleWishlist(e, product.id)}
            className={`wl-btn prod-wish${inWish ? ' is-on' : ''}`}
            aria-label={inWish ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            {inWish ? <HeartIconSolid style={{ width: 16, height: 16, color: 'white' }} /> : <HeartIcon style={{ width: 16, height: 16, color: '#7c3aed' }} />}
          </button>

          {hasMulti && (
            <>
              <button onClick={e => prevMedia(e, product.id, mediaItems.length)} className="prod-nav-btn" style={{ left: 6 }} aria-label="Previous">
                <ChevronLeftIcon style={{ width: 14, height: 14, color: '#555' }} />
              </button>
              <button onClick={e => nextMedia(e, product.id, mediaItems.length)} className="prod-nav-btn" style={{ right: 6 }} aria-label="Next">
                <ChevronRightIcon style={{ width: 14, height: 14, color: '#555' }} />
              </button>
              <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4, zIndex: 5 }}>
                {mediaItems.map((media, ii) => (
                  <button
                    key={ii}
                    onClick={e => gotoMedia(e, product.id, ii)}
                    aria-label={`Show media ${ii + 1}`}
                    style={{ height: 5, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.2s ease', width: ii === cur ? 14 : 5, background: ii === cur ? 'white' : 'rgba(255,255,255,0.6)' }}
                  />
                ))}
              </div>
            </>
          )}

          {lowStock && (
            <div className="prod-chip-warn" style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 5 }}>
              Only {product.stock} left
            </div>
          )}

          {product.stock === 0 && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(4px)', zIndex: 6 }}>
              <span style={{ padding: '4px 10px', borderRadius: 40, background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: 10, fontWeight: 600 }}>Out of Stock</span>
            </div>
          )}
        </div>
      </Link>

      <div className="prod-info">
        <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', display: 'block' }} onClick={() => window.scrollTo(0, 0)}>
          {/* [rating hidden] <div className="prod-rate-row">
            {rating.count > 0 ? (
              <span className="prod-rate">
                <StarIconSolid style={{ width: 11, height: 11, color: '#f59e0b' }} />
                {rating.average.toFixed(1)}
                <i>({rating.count})</i>
              </span>
            ) : (
              <span className="prod-rate prod-rate-new">New</span>
            )}
          </div> */}
          <h3 className="prod-title">{product.name}</h3>

          {(product.categories?.[0] || ageGroupName) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
              {product.categories?.slice(0, 1).map(cat => (
                <span key={cat.id} className="prod-badge-pill">{cat.name}</span>
              ))}
              {ageGroupName && (
                <span className="prod-age-pill">
                  <CakeIcon style={{ width: 8, height: 8 }} />
                  {ageGroupName.length > 12 ? ageGroupName.slice(0, 10) + '...' : ageGroupName}
                </span>
              )}
            </div>
          )}
        </Link>

        <div className="prod-foot">
          <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', minWidth: 0 }} onClick={() => window.scrollTo(0, 0)}>
            <div className="prod-price">₹{product.selling_price || product.mrp}</div>
            <div className="prod-mrp-row">
              {save > 0 && (
                <>
                  <s>₹{product.mrp}</s>
                  <span className="prod-save">Save ₹{save}</span>
                </>
              )}
            </div>
          </Link>
          <Link to={`/products/${product.id}`} className="prod-go" aria-label={`View ${product.name}`} onClick={() => window.scrollTo(0, 0)}>
            <ChevronRightIcon style={{ width: 16, height: 16 }} />
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════ */
const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<ProductWithRating[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [curMediaIdx, setCurMediaIdx] = useState<{ [k: number]: number }>({});
  const [wishlist, setWishlist] = useState<number[]>([]);
  const [loading, setLoading] = useState({ products: true, categories: true, ageGroups: true, slides: true });
  const [error, setError] = useState({ products: null as string | null, categories: null as string | null, ageGroups: null as string | null, slides: null as string | null });
  const [productRatings, setProductRatings] = useState<Record<number, { average: number; count: number }>>({});
  const [ratingsLoading, setRatingsLoading] = useState<Set<number>>(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    url: string;
    type: 'image' | 'video' | 'pdf';
    title: string;
  }>({
    isOpen: false,
    url: '',
    type: 'image',
    title: ''
  });

  useEffect(() => {
    const check = () => { setIsMobile(window.innerWidth < 640); setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024); };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const fetchProductRating = async (productId: number) => {
    if (ratingsLoading.has(productId)) return;
    setRatingsLoading(prev => new Set(prev).add(productId));
    try {
      const response = await api.getProductReviews(productId, { page: 1, page_size: 1 });
      if (response.success && response.data) {
        setProductRatings(prev => ({ ...prev, [productId]: { average: response.data.summary.average_rating || 0, count: response.data.summary.total_reviews || 0 } }));
      }
    } catch {}
    finally {
      setRatingsLoading(prev => { const s = new Set(prev); s.delete(productId); return s; });
    }
  };

  useEffect(() => {
    if (featuredProducts.length > 0) {
      featuredProducts.forEach(p => { if (!productRatings[p.id]) fetchProductRating(p.id); });
    }
  }, [featuredProducts]);

  const getMediaCount = (product: ProductWithRating): number => {
    let count = (product.images?.length || 0);
    count += (product.videos?.length || 0);
    if ((product as any).size_chart) count += 1;
    return count;
  };

  const prevMedia = (e: React.MouseEvent, id: number, total: number) => {
    e.preventDefault(); e.stopPropagation();
    setCurMediaIdx(p => ({ ...p, [id]: ((p[id] || 0) - 1 + total) % total }));
  };

  const nextMedia = (e: React.MouseEvent, id: number, total: number) => {
    e.preventDefault(); e.stopPropagation();
    setCurMediaIdx(p => ({ ...p, [id]: ((p[id] || 0) + 1) % total }));
  };

  const gotoMedia = (e: React.MouseEvent, id: number, i: number) => {
    e.preventDefault(); e.stopPropagation();
    setCurMediaIdx(p => ({ ...p, [id]: i }));
  };

  const toggleWishlist = (e: React.MouseEvent, id: number) => { e.preventDefault(); e.stopPropagation(); setWishlist(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); };

  const getImageUrl = useCallback((imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) return imageUrl;
    const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace('/api', '');
    let c = imageUrl.replace(/^[./]+/, '');
    if (c.includes('uploads/')) { const m = c.match(/uploads\/(.+)$/); if (m) c = `uploads/${m[1]}`; } else { c = `uploads/${c}`; }
    return `${BASE}/${c}`.replace(/([^:]\/)\/+/g, '$1');
  }, []);

  const nextSlide = useCallback(() => { if (slides.length > 0) setCurrentSlideIndex(p => (p + 1) % slides.length); }, [slides.length]);
  const prevSlide = useCallback(() => { if (slides.length > 0) setCurrentSlideIndex(p => (p - 1 + slides.length) % slides.length); }, [slides.length]);
  const goToSlide = useCallback((i: number) => setCurrentSlideIndex(i), []);

  useEffect(() => { if (slides.length <= 1) return; const iv = setInterval(nextSlide, 5000); return () => clearInterval(iv); }, [slides.length, nextSlide]);

  const loadSlides = useCallback(async () => {
    try {
      const r = await api.getActiveSlides();
      const d: Slide[] = r.success && Array.isArray(r.data) ? r.data : Array.isArray(r) ? r : r.data || [];
      setSlides(d.filter((s: any) => s.is_active).sort((a: any, b: any) => a.order - b.order));
    } catch { setError(p => ({ ...p, slides: 'Unable to load slider' })); }
    finally { setLoading(p => ({ ...p, slides: false })); }
  }, []);

  const PRODUCTS_PER_LOAD = 12;

  const buildMediaIdx = (list: ProductWithRating[]) => {
    const ix: any = {};
    list.forEach((p: ProductWithRating) => {
      const totalMedia = (p.images?.length || 0) + (p.videos?.length || 0) + ((p as any).size_chart ? 1 : 0);
      if (totalMedia > 1) ix[p.id] = 0;
    });
    return ix;
  };

  // page 1 => replace list, page > 1 => append (Load More)
  const fetchProductsPage = useCallback(async (page: number) => {
    const isFirst = page === 1;
    if (!isFirst) setLoadingMore(true);
    try {
      const r: any = await api.getProducts({ page, page_size: PRODUCTS_PER_LOAD });
      const d: ProductWithRating[] = r.success && Array.isArray(r.data) ? r.data : Array.isArray(r) ? r : r.data || [];
      const pg = r.pagination;

      setFeaturedProducts(prev => {
        if (isFirst) return d;
        const seen = new Set(prev.map(x => x.id));
        return [...prev, ...d.filter(x => !seen.has(x.id))];
      });
      setCurMediaIdx(prev => (isFirst ? buildMediaIdx(d) : { ...buildMediaIdx(d), ...prev }));

      setProductPage(page);
      if (pg && typeof pg.total === 'number') setTotalProducts(pg.total);
      setHasMoreProducts(
        pg && typeof pg.total_pages === 'number' ? page < pg.total_pages : d.length === PRODUCTS_PER_LOAD
      );
    } catch {
      if (isFirst) setError(p => ({ ...p, products: 'Unable to load products' }));
    } finally {
      if (isFirst) setLoading(p => ({ ...p, products: false }));
      else setLoadingMore(false);
    }
  }, []);

  const loadFeaturedProducts = useCallback(() => fetchProductsPage(1), [fetchProductsPage]);
  const handleLoadMore = () => { if (!loadingMore && hasMoreProducts) fetchProductsPage(productPage + 1); };

  // Auto-load: when the bottom of the list (the "Load More" area) comes into view, load the next batch.
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const handleLoadMoreRef = useRef(handleLoadMore);
  handleLoadMoreRef.current = handleLoadMore;
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMoreProducts || loadingMore || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) handleLoadMoreRef.current(); },
      { rootMargin: '300px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMoreProducts, loadingMore, productPage]);

  useEffect(() => { loadSlides(); loadFeaturedProducts(); window.scrollTo(0, 0); }, []);

  const handleMediaClick = (url: string, type: 'image' | 'video' | 'pdf', title: string) => {
    setPreviewModal({ isOpen: true, url, type, title });
  };

  const features = [
    { icon: TruckIcon, title: 'Free Shipping', color: '#ec4899', bg: 'linear-gradient(135deg,#fdf2f8,#fce7f3)' },
    { icon: ShieldCheckIcon, title: 'Secure Payment', desc: '100% secure transactions', color: '#a855f7', bg: 'linear-gradient(135deg,#faf5ff,#f3e8ff)' },
    { icon: ArrowPathIcon, title: 'Easy Returns',  color: '#10b981', bg: 'linear-gradient(135deg,#ecfdf5,#d1fae5)' },
    { icon: ClockIcon, title: '24/7 Support', desc: 'Dedicated customer service', color: '#f59e0b', bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)' }
  ];


  if (loading.slides && loading.products) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#fdf2f8,#faf5ff)' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: 'center' }}>
          <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 20px' }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid #fce7f3', borderTopColor: '#ec4899', borderRightColor: '#a855f7', animation: 'spin 0.9s linear infinite' }} />
            <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', background: 'linear-gradient(135deg,#ec4899,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SparklesIcon style={{ width: 24, height: 24, color: 'white' }} />
            </div>
          </div>
          <p style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#6b7280', fontWeight: 500 }}>Loading amazing things…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fdfbff', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
        mediaUrl={previewModal.url}
        mediaType={previewModal.type}
        title={previewModal.title}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;0,800;1,700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Sora:wght@400;500;600;700;800&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        @keyframes spin       { to { transform: rotate(360deg); } }
        @keyframes fadeUp     { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes kenBurns   { from { transform:scale(1); } to { transform:scale(1.07); } }
        @keyframes gradShift  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes shimmer    { 0%{background-position:-500px 0} 100%{background-position:500px 0} }
        @keyframes float      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }

        .gtext     { background:linear-gradient(135deg,#db2777,#a855f7); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .gtext-rev { background:linear-gradient(135deg,#9333ea,#ec4899); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

        .skel { background:linear-gradient(90deg,#f5f0fa 0%,#ede8f5 40%,#f5f0fa 80%); background-size:500px 100%; animation:shimmer 1.4s infinite; border-radius:12px; }

        .hero-txt > * { opacity:0; transform:translateY(20px); animation:fadeUp 0.7s cubic-bezier(0.22,1,0.36,1) forwards; }
        .hero-txt > *:nth-child(1){animation-delay:.08s}
        .hero-txt > *:nth-child(2){animation-delay:.2s}
        .hero-txt > *:nth-child(3){animation-delay:.32s}
        .hero-txt > *:nth-child(4){animation-delay:.44s}

        .ken-burns { animation:kenBurns 22s ease-out both; }
        .anim-up { opacity:0; transform:translateY(22px); animation:fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) forwards; }
        .anim-d1{animation-delay:0.04s} .anim-d2{animation-delay:0.10s} .anim-d3{animation-delay:0.16s}
        .anim-d4{animation-delay:0.22s} .anim-d5{animation-delay:0.28s} .anim-d6{animation-delay:0.34s}

        .prod-card {
          background: #ffffff;
          border-radius: 26px;
          padding: 8px;
          border: 1px solid #f3ecf9;
          flex-shrink: 0;
          box-shadow: 0 1px 2px rgba(31,23,51,0.04), 0 10px 28px -14px rgba(124,58,237,0.16);
          transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease, border-color 0.25s;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .prod-card:hover {
          transform: translateY(-6px);
          border-color: #e9d5ff;
          box-shadow: 0 2px 4px rgba(31,23,51,0.04), 0 26px 50px -18px rgba(139,92,246,0.34);
        }
        .prod-card:hover .prod-nav-btn { opacity: 1 !important; }
        .prod-card:hover .prod-img-track img { transform: scale(1.06); }
        .prod-card:hover .prod-go { background: linear-gradient(135deg,#ec4899,#8b5cf6); transform: rotate(-45deg); box-shadow: 0 8px 18px -4px rgba(139,92,246,0.5); }

        .prod-media {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
          border-radius: 20px;
          background: radial-gradient(circle at 28% 18%, #ffffff 0%, #fdf2f8 42%, #f3e8ff 100%);
        }
        .prod-off {
          position: absolute; top: 10px; left: 10px; z-index: 5;
          background: #1f1733; color: #fff;
          font-size: 10px; font-weight: 600; letter-spacing: 0.4px;
          padding: 5px 10px; border-radius: 999px;
          display: inline-flex; align-items: baseline; gap: 3px;
        }
        .prod-off b { font-size: 12px; font-weight: 800; color: #f9a8d4; }
        .prod-wish {
          position: absolute; top: 8px; right: 8px; z-index: 5;
          width: 34px; height: 34px; border-radius: 50%;
          background: rgba(255,255,255,0.9); backdrop-filter: blur(8px);
          border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; box-shadow: 0 4px 14px rgba(31,23,51,0.12);
        }
        .prod-wish.is-on { background: linear-gradient(135deg,#ec4899,#8b5cf6); border-color: transparent; }
        .prod-nav-btn {
          position: absolute; top: 50%; transform: translateY(-50%); z-index: 5;
          width: 28px; height: 28px; border-radius: 50%;
          background: rgba(255,255,255,0.9); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          opacity: 0.7; transition: opacity 0.2s;
        }
        .prod-chip-dark {
          background: rgba(31,23,51,0.72); backdrop-filter: blur(6px); color: #fff;
          padding: 3px 9px; border-radius: 999px; font-size: 9px; font-weight: 700;
          display: inline-flex; align-items: center; gap: 4px;
        }
        .prod-chip-warn {
          background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa;
          padding: 3px 9px; border-radius: 999px; font-size: 9.5px; font-weight: 700;
        }

        .prod-info { padding: 12px; display: flex; flex-direction: column; flex: 1; }
        .prod-rate-row { margin-bottom: 8px; min-height: 22px; }
        .prod-rate {
          display: inline-flex; align-items: center; gap: 4px;
          background: #fffbeb; color: #92400e; border: 1px solid #fde68a;
          font-size: 11px; font-weight: 700; padding: 2px 9px 2px 7px; border-radius: 999px;
        }
        .prod-rate i { font-style: normal; font-weight: 500; color: #b45309; opacity: 0.7; }
        .prod-rate-new { background: #f5f3ff; color: #6d28d9; border-color: #ddd6fe; padding: 2px 10px; letter-spacing: 0.3px; }
        .prod-title {
          margin: 0 0 6px; color: #1f1733; font-weight: 600; font-size: 13.5px; line-height: 1.4;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
          min-height: 34px; font-family: 'Sora', sans-serif;
        }
        .prod-badge-pill {
          padding: 1px 6px; border-radius: 100px; font-size: 8px; font-weight: 500;
          background: linear-gradient(135deg, rgba(255,105,180,0.12), rgba(75,0,130,0.12));
          border: 1px solid rgba(255,105,180,0.2); color: #7b1fa2; font-family: 'Sora', sans-serif;
        }
        .prod-age-pill {
          display: inline-flex; align-items: center; gap: 2px;
          padding: 1px 6px; border-radius: 100px; font-size: 8px; font-weight: 500;
          background: rgba(138,43,226,0.08); border: 1px solid rgba(138,43,226,0.15); color: #7b1fa2;
          font-family: 'Sora', sans-serif;
        }
        .prod-foot { margin-top: auto; display: flex; align-items: flex-end; justify-content: space-between; gap: 8px; }
        .prod-price { color: #1f1733; font-size: 19px; font-weight: 800; line-height: 1.1; letter-spacing: -0.3px; font-family: 'Sora', sans-serif; }
        .prod-mrp-row { display: flex; align-items: center; gap: 6px; margin-top: 4px; min-height: 17px; flex-wrap: wrap; }
        .prod-mrp-row s { color: #a8a3b8; font-size: 11px; }
        .prod-save { background: #ecfdf5; color: #047857; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 999px; }
        .prod-go {
          flex-shrink: 0; width: 38px; height: 38px; border-radius: 50%;
          background: #1f1733; color: #fff;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), background 0.3s, box-shadow 0.3s;
        }
        @media (max-width: 640px) {
          .prod-card { border-radius: 20px; padding: 6px; }
          .prod-media { border-radius: 15px; }
          .prod-go { width: 30px; height: 30px; }
          .prod-save { display: none; }
        }
        @media (max-width: 480px) {
          .prod-info { padding: 10px; }
          .prod-title { font-size: 12px; }
          .prod-price { font-size: 16px; }
          .prod-nav-btn { width: 28px; height: 28px; }
        }

        .wl-btn { transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1) !important; }
        .wl-btn:hover { transform: scale(1.18) !important; }

        .feat-card {
          background: white;
          border-radius: 20px;
          border: 1.5px solid #f0eaf8;
          transition: transform 0.28s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.28s ease, border-color 0.2s;
          box-shadow: 0 2px 10px rgba(168,85,247,0.04);
        }
        .feat-card:hover {
          transform: translateY(-5px);
          border-color: #fce7f3;
          box-shadow: 0 12px 28px -8px rgba(236,72,153,0.12);
        }
        .feat-card:hover .feat-ico { transform: scale(1.16) rotate(6deg) !important; }
        .feat-ico { transition: transform 0.32s cubic-bezier(0.34,1.56,0.64,1) !important; }

        .age-pill {
          border-radius: 50%;
          overflow: hidden;
          cursor: pointer;
          text-decoration: none;
          display: block;
          transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s ease;
          position: relative;
          box-shadow: 0 6px 20px rgba(0,0,0,0.1);
        }
        .age-pill:hover { transform: translateY(-4px) scale(1.04); box-shadow: 0 12px 32px rgba(0,0,0,0.15); }

        .cat-card-v2 {
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          text-decoration: none;
          display: block;
          position: relative;
          transition: transform 0.35s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.35s ease;
          box-shadow: 0 4px 18px rgba(168,85,247,0.08);
        }
        .cat-card-v2:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 22px 52px -10px rgba(168,85,247,0.22);
        }
        .cat-card-v2:hover .cat-img-v2 { transform: scale(1.09) !important; }
        .cat-img-v2 { transition: transform 0.52s cubic-bezier(0.25,0.46,0.45,0.94) !important; }
        .cat-card-v2:hover .cat-shine-v2 { opacity: 1 !important; }
        .cat-shine-v2 { transition: opacity 0.35s ease; }
        .cat-card-v2:hover .cat-tag-v2 { transform: scale(1.06) !important; }
        .cat-tag-v2 { transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1) !important; }

        .view-all-cats-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 15px 40px;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px;
          font-weight: 700;
          background: linear-gradient(135deg, #ec4899, #a855f7);
          color: white;
          box-shadow: 0 8px 28px -6px rgba(236,72,153,0.42);
          transition: all 0.3s cubic-bezier(0.34,1.2,0.64,1);
          text-decoration: none;
          letter-spacing: 0.2px;
        }
        .view-all-cats-btn:hover {
          transform: translateY(-4px) scale(1.04);
          box-shadow: 0 16px 36px -8px rgba(236,72,153,0.52);
          background: linear-gradient(135deg, #db2777, #9333ea);
        }

        .gbtn {
          background: linear-gradient(135deg, #ec4899, #a855f7);
          color: white;
          border: none;
          cursor: pointer;
          font-weight: 700;
          font-family: 'Plus Jakarta Sans', sans-serif;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.28s cubic-bezier(0.34,1.2,0.64,1);
          box-shadow: 0 6px 20px -6px rgba(236,72,153,0.4);
        }
        .gbtn:hover:not(:disabled) {
          background: linear-gradient(135deg, #db2777, #9333ea);
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 12px 24px -6px rgba(236,72,153,0.45);
        }
        .gbtn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; box-shadow: none; }

        .sl-arrow {
          width: 40px; height: 40px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.45);
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
          flex-shrink: 0;
        }
        .sl-arrow:hover:not(:disabled) { background: rgba(255,255,255,0.28); border-color: rgba(255,255,255,0.85); }
        .sl-arrow:disabled { opacity: 0.3; cursor: default; }
        .sl-arrow svg { width: 17px; height: 17px; color: white; }

        .hero-dot { height: 6px; border-radius: 99px; border: none; padding: 0; cursor: pointer; transition: all 0.3s ease; background: rgba(255,255,255,0.4); }
        .hero-dot.active { width: 26px !important; background: white; }
        .page-dot { height: 7px; border-radius: 99px; border: none; padding: 0; cursor: pointer; transition: all 0.3s ease; background: #f0e8fa; }
        .page-dot.active { background: linear-gradient(90deg,#ec4899,#a855f7); }

        .sec-pill { display:inline-flex; align-items:center; gap:6px; padding:5px 14px; border-radius:99px; font-size:12px; font-weight:700; }

        .txt-link {
          font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:700;
          color:#db2777; text-decoration:none; cursor:pointer; background:none; border:none;
          padding:8px 16px; display:inline-flex; align-items:center; gap:5px;
          border-radius:99px; border:1.5px solid #fce7f3;
          transition:all 0.22s cubic-bezier(0.34,1.56,0.64,1);
        }
        .txt-link:hover { background:#fdf2f8; border-color:#f9a8d4; transform:translateY(-1px); }

        .contact-bg {
          background: linear-gradient(135deg,#ec4899,#a855f7,#6366f1);
          background-size: 200% 200%;
          animation: gradShift 10s ease infinite;
        }

        .scroll-hint { animation: float 2.2s ease-in-out infinite; }
        .mob-track { display:flex; transition:transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94); will-change:transform; }
        .section-divider { width: 48px; height: 3px; border-radius: 99px; background: linear-gradient(90deg,#ec4899,#a855f7); margin: 0 0 20px; }
        .cat-overlay { position:absolute; inset:0; background:linear-gradient(0deg,rgba(168,85,247,0.22) 0%,transparent 60%); opacity:0; transition:opacity 0.3s; z-index:1; }
      `}</style>

      {/* ── HERO SLIDER ─────────────────────────── */}
      <section style={{
        position: 'relative',
        height: isMobile ? 'auto' : 'min(800px,96vh)',
        aspectRatio: isMobile ? '16/10' : undefined,
        minHeight: isMobile ? '240px' : undefined,
        maxHeight: isMobile ? '60vh' : undefined,
        overflow: 'hidden',
        background: '#f5f0fa'
      }}>
        {(slides.length > 0 ? slides : [null as any]).map((slide: any, i: number) => (
          <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === currentSlideIndex ? 1 : 0, pointerEvents: i === currentSlideIndex ? 'auto' : 'none', transition: 'opacity 1.1s ease' }}>

            {/* IMAGE — full bleed cover */}
            {slide?.image_url ? (
              <img
                src={getImageUrl(slide.image_url)}
                alt={slide.title || ''}
                className="ken-burns"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  display: 'block'
                }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#faf0f8,#f3e8ff)' }} />
            )}

            {/* DARK OVERLAY — text readable */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.20) 45%, rgba(0,0,0,0.70) 100%)' }} />

            {/* TEXT — vertically centered so nothing (incl. the button) gets clipped */}
            {i === currentSlideIndex && (
              <div style={{
                position: 'relative',
                height: '100%',
                maxWidth: 1280,
                margin: '0 auto',
                padding: isMobile ? '0 18px' : '0 clamp(20px,6vw,72px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}>
                <div className="hero-txt" style={{ maxWidth: isMobile ? '100%' : 560 }}>
                  <h1 style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: isMobile ? 'clamp(1.05rem,4.8vw,1.5rem)' : 'clamp(2.4rem,5.5vw,4.2rem)',
                    fontWeight: 800,
                    color: 'white',
                    lineHeight: 1.2,
                    marginBottom: isMobile ? 10 : 18,
                    textShadow: '0 2px 24px rgba(0,0,0,0.5)'
                  }}>
                    {slide?.title || 'Welcome to Twinklefanta'}
                  </h1>
                  {slide?.description && !isMobile && (
                    <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.9rem,1.8vw,1.05rem)', lineHeight: 1.7, marginBottom: 30, maxWidth: 440 }}>
                      {slide.description}
                    </p>
                  )}
                  <Link to={slide?.button_link || '/products'} className="gbtn"
                    style={{
                      padding: isMobile ? '9px 18px' : '15px 32px',
                      borderRadius: 12,
                      fontSize: isMobile ? 12 : 15,
                      background: 'white',
                      color: '#db2777',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                    }}
                    onMouseEnter={e => { const el = e.currentTarget; el.style.background = 'linear-gradient(135deg,#ec4899,#a855f7)'; el.style.color = 'white'; }}
                    onMouseLeave={e => { const el = e.currentTarget; el.style.background = 'white'; el.style.color = '#db2777'; }}>
                    {slide?.button_text || 'Shop Now'} <ChevronRightIcon style={{ width: isMobile ? 13 : 18, height: isMobile ? 13 : 18 }} />
                  </Link>
                </div>
              </div>
            )}
          </div>
        ))}

        {slides.length > 1 && (
          <>
            {!isMobile && (
              <>
                <button onClick={prevSlide} className="sl-arrow" style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)', zIndex: 30 }}><ChevronLeftIcon /></button>
                <button onClick={nextSlide} className="sl-arrow" style={{ position: 'absolute', right: 22, top: '50%', transform: 'translateY(-50%)', zIndex: 30 }}><ChevronRightIcon /></button>
              </>
            )}
            <div style={{ position: 'absolute', bottom: isMobile ? 10 : 30, left: '50%', transform: 'translateX(-50%)', zIndex: 30, display: 'flex', gap: isMobile ? 5 : 8, alignItems: 'center' }}>
              {slides.map((_, i) => (
                <button key={i} onClick={() => goToSlide(i)} className={`hero-dot ${i === currentSlideIndex ? 'active' : ''}`}
                  style={{ width: i === currentSlideIndex ? (isMobile ? 16 : 26) : isMobile ? 5 : 7 }} />
              ))}
            </div>
          </>
        )}

        {!isMobile && (
          <div className="scroll-hint" style={{ position: 'absolute', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, opacity: 0.45 }}>
            <div style={{ width: 1.5, height: 38, background: 'white', borderRadius: 99 }} />
            <span style={{ color: 'white', fontSize: 8.5, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', writingMode: 'vertical-lr' }}>Scroll</span>
          </div>
        )}
      </section>

      {/* ── BODY ────────────────────────────────── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '36px 16px 0' : '56px clamp(20px,4vw,52px) 0' }}>

        {/* FEATURES */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 14 : 20, marginBottom: isMobile ? 52 : 88 }}>
          {features.map((f, i) => (
            <div key={i} className={`feat-card anim-up anim-d${i + 1}`}
              style={{ padding: isMobile ? '22px 14px' : '30px 24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 12 : 16 }}>
              <div className="feat-ico" style={{ width: isMobile ? 44 : 54, height: isMobile ? 44 : 54, borderRadius: isMobile ? 14 : 16, background: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <f.icon style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, color: f.color }} />
              </div>
              <div>
                <p style={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? 13.5 : 15, marginBottom: 3, lineHeight: 1.3 }}>{f.title}</p>
                <p style={{ color: '#7c7c96', fontSize: isMobile ? 11.5 : 13, lineHeight: 1.4, margin: 0 }}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ALL PRODUCTS */}
        <section style={{ marginBottom: isMobile ? 52 : 88 }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="sec-pill anim-up anim-d1" style={{ background: 'linear-gradient(135deg,rgba(168,85,247,0.09),rgba(236,72,153,0.07))', color: '#9333ea', border: '1px solid rgba(168,85,247,0.18)', marginBottom: 10, fontSize: isMobile ? 11 : 12 }}>
                <SparklesIcon style={{ width: 12, height: 12 }} /> All Products
              </div>
              <h2 className="anim-up anim-d2 gtext-rev" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 'clamp(1.4rem,5vw,1.75rem)' : 'clamp(1.8rem,3.5vw,2.8rem)', fontWeight: 800, margin: 0, lineHeight: 1.1 }}>
                Shop Our Collection
              </h2>
            </div>
          </div>

          {loading.products ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(260px,1fr))', gap: isMobile ? 12 : 22 }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ borderRadius: 20, overflow: 'hidden', border: '1.5px solid #f0eaf8', background: 'white' }}>
                  <div className="skel" style={{ aspectRatio: '1', borderRadius: 0 }} />
                  <div style={{ padding: isMobile ? '12px' : '18px', background: 'white', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="skel" style={{ height: isMobile ? 12 : 15, width: '78%' }} />
                    <div className="skel" style={{ height: isMobile ? 10 : 13, width: '50%' }} />
                    <div className="skel" style={{ height: isMobile ? 36 : 42, borderRadius: 12 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(260px,1fr))', gap: isMobile ? 12 : 22 }}>
              {featuredProducts.map((product: ProductWithRating, idx: number) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  idx={idx}
                  curMediaIdx={curMediaIdx}
                  wishlist={wishlist}
                  getImageUrl={getImageUrl}
                  toggleWishlist={toggleWishlist}
                  prevMedia={prevMedia}
                  nextMedia={nextMedia}
                  gotoMedia={gotoMedia}
                  productRatings={productRatings}
                  onMediaClick={handleMediaClick}
                />
              ))}
            </div>
          )}

          {!loading.products && featuredProducts.length > 0 && (
            <div ref={loadMoreRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: isMobile ? 28 : 44 }}>
              {totalProducts > 0 && (
                <p style={{ margin: 0, fontSize: isMobile ? 12 : 13.5, color: '#7c7c96' }}>
                  Showing {featuredProducts.length} of {totalProducts} products
                </p>
              )}
              {hasMoreProducts && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="gbtn"
                  style={{
                    padding: isMobile ? '12px 32px' : '15px 44px',
                    borderRadius: 50,
                    border: 'none',
                    fontSize: isMobile ? 14 : 15.5,
                    fontWeight: 700,
                    color: 'white',
                    background: 'linear-gradient(135deg,#ec4899,#a855f7)',
                    boxShadow: '0 8px 24px rgba(236,72,153,0.3)',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    opacity: loadingMore ? 0.7 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          )}
        </section>

        {/* CONTACT SECTION */}
        <section style={{ marginBottom: isMobile ? 40 : 72 }}>
          <div className="contact-bg" style={{ borderRadius: isMobile ? 24 : 32, padding: isMobile ? '36px 22px' : 'clamp(48px,6vw,72px)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -48, right: -48, width: isMobile ? 160 : 240, height: isMobile ? 160 : 240, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ position: 'absolute', bottom: -60, left: -24, width: isMobile ? 200 : 280, height: isMobile ? 200 : 280, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <div style={{ position: 'absolute', top: '50%', right: '8%', transform: 'translateY(-50%)', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

            <div style={{ position: 'relative', color: 'white', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
              <div className="sec-pill" style={{ background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.22)', color: 'white', marginBottom: 16, display: 'inline-flex', fontSize: isMobile ? 11 : 12 }}>
                <EnvelopeIcon style={{ width: 12, height: 12 }} /> Get in Touch
              </div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 'clamp(1.45rem,5vw,1.8rem)' : 'clamp(1.8rem,4vw,2.7rem)', fontWeight: 800, marginBottom: 14, textShadow: '0 2px 20px rgba(0,0,0,0.12)', lineHeight: 1.15 }}>
                We'd Love to Hear From You
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: isMobile ? '0.85rem' : '0.97rem', marginBottom: 32, lineHeight: 1.65, maxWidth: 480, margin: '0 auto 32px' }}>
                {isMobile ? "Have questions? We're here to help!" : "Have questions, feedback, or special requests? Our team is here to help!"}
              </p>
              <Link to="/contact" className="gbtn"
                style={{ padding: isMobile ? '13px 28px' : '16px 38px', borderRadius: 50, fontSize: isMobile ? 14.5 : 16, background: 'white', color: '#db2777', boxShadow: '0 10px 28px rgba(0,0,0,0.18)', display: 'inline-flex' }}
                onMouseEnter={e => { const el = e.currentTarget; el.style.background = 'linear-gradient(135deg,#fff0f6,#f5eeff)'; el.style.transform = 'translateY(-3px) scale(1.03)'; }}
                onMouseLeave={e => { const el = e.currentTarget; el.style.background = 'white'; el.style.transform = 'translateY(0) scale(1)'; }}>
                Contact Us Today <ChevronRightIcon style={{ width: isMobile ? 16 : 18, height: isMobile ? 16 : 18 }} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;